import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useLazyDataModel,
  params,
  arrayOf as arrayType,
  number as numberType,
  string as stringType,
  customType
} from '@thd-nucleus/data-sources';

export const useSubtotalFetcher = ({
  items,
  storeId,
  isResolvingIntents
}) => {
  const [groupTotal, setGroupTotal] = useState(null);
  const subTotalLoadedItemIdsRef = useRef([]);

  // Filter to get only catalog products (items with itemId that are not pure intents)
  const catalogProducts = useMemo(() => {
    return items?.filter((item) => item.itemId) || [];
  }, [items]);

  const [fetchSubTotal, subTotalResponse] = useLazyDataModel('priceSubtotal', {
    variables: { storeId },
    context: { withAuth: true }
  });

  const { data: subTotalData, error: subtotalError, loading: subtotalLoading } = subTotalResponse || {};

  // Check if subtotal was already fetched for these items
  const checkAlreadyFetched = (itemIds) => {
    const subTotalAlreadyLoadedIds = subTotalLoadedItemIdsRef.current || [];
    return itemIds.every((id) => subTotalAlreadyLoadedIds.includes(id));
  };

  // Count items that still need resolution excluding those that have failed
  const getUnresolvedItemsCount = () => {
    return (items || [])
      .filter((item) => !item.itemId && item.intent)
      .filter((item) => !item.resolveFailed)
      .length || 0;
  };

  // Fetch subtotal when catalog products change
  useEffect(() => {
    const fetchTotal = async () => {
      const lineItems = catalogProducts
        .map((product) => ({ itemId: product.itemId, quantity: product.quantity || 1 }))
        .filter((item) => item.itemId);

      const itemIds = lineItems.map((item) => item.itemId);
      const alreadyFetched = lineItems.length === itemIds.length && checkAlreadyFetched(itemIds);
      const itemsToBeResolved = getUnresolvedItemsCount();

      if (!alreadyFetched && lineItems?.length && itemsToBeResolved === 0 && !subtotalLoading) {
        subTotalLoadedItemIdsRef.current = [...subTotalLoadedItemIdsRef.current, ...itemIds];
        await fetchSubTotal({ variables: { lineItems } });
      }
    };

    if (catalogProducts?.length) {
      fetchTotal();
    } else if (!isResolvingIntents) {
      setGroupTotal(0);
    }
  }, [catalogProducts, subtotalLoading, setGroupTotal, fetchSubTotal, items]);

  // Update group total when subtotal data changes
  useEffect(() => {
    if (subTotalData) {
      setGroupTotal(subTotalData?.priceSubtotal?.subtotal || 0);
    } else if (subtotalError) {
      setGroupTotal(0);
    }
  }, [subTotalData, subtotalError, setGroupTotal]);

  return {
    groupTotal,
    subtotalLoading,
    subtotalError
  };
};

useSubtotalFetcher.dataModel = {
  priceSubtotal: params({
    lineItems: arrayType(customType('LineItem').shape({
      itemId: stringType(),
      quantity: numberType()
    })).isRequired(),
    storeId: stringType().isRequired()
  }).shape({
    subtotal: numberType()
  })
};
